import Cookies from "js-cookie"
import { numberOnly } from "../../masks/masks"
import { VOUCHER_QUERY_NAMES } from "../constants"
import {
  CHECKOUT_LAYOUT_ALL,
  TCheckoutComponent,
  TCheckoutLayout,
  TCheckoutLayoutComponent,
  TCheckoutOrderAttempt,
} from "../types/Checkout"
import { IOrderCharge, IOrderResponse } from "../types/Order"
import {
  IProductPrice,
  TPaymentMethodEnum,
  TPriceAffiliated,
  TProductDelivery,
  TProductPixel,
} from "../types/Product"
import { TSale, TSaleItemStorage, TSaleStorage } from "../types/Sale"
import { TMetaUser } from "../types/Meta"
import Convertions from "./convertions"
import { addDays, addMinutes, diffMinutes, getMaxDate } from "./dateTime"
import {
  TCustomCheckoutComponentItem,
  TCustomCheckoutComponentType,
} from "../types/CustomCheckout"
import NumberFunctions from "./NumberFunctions"

const getSaleDefault = (
  query: URLSearchParams,
  produtctPriceId: string | undefined,
  onlyProducer: boolean,
  subscriptionCodeId: string | null | undefined,
  methodType?: TPaymentMethodEnum
): TSale => {
  const purpose = query.get("purpose")
  return {
    checkoutOrderId: undefined,
    productPriceId: produtctPriceId ?? "",
    quantity: 1,
    affiliateCode: query.get("afid"),
    name: query.get("name") ?? "",
    document: "",
    phone: query.get("phone") ?? "",
    email: query.get("email") ?? "",
    emailConfirm: "",
    onlyProducer,
    address: {
      city: "",
      state: "",
      street: "",
      number: "",
      zipCode: query.get("zipcode") || "",
      complement: "",
      reference: "",
      neighborhood: "",
    },
    payment: {
      methodType: methodType ?? TPaymentMethodEnum.CreditCard,
      description: "",
      installment: 0,
      value: 0,
      discount: 0,
      total: 0,
      voucherStatus: "none",
      subtotal: 0.0,
      payments: [],
    },
    products: [],
    src: query.get("src"),
    sck: query.get("sck"),
    utm_campaign: query.get("utm_campaign"),
    utm_source: query.get("utm_source"),
    utm_medium: query.get("utm_medium"),
    utm_content: query.get("utm_content"),
    utm_term: query.get("utm_term"),
    pageAccessId: null,
    recaptchaToken: null,
    isPurposeCardUpdate: isCheckoutCardUpdate(purpose),
    isCardUpdateOnly: isCheckoutCardUpdateOnly(purpose),
    subscriptionCodeId,
  }
}

const getVoucherAuto = (query: URLSearchParams) => {
  for (let i = 0; i < VOUCHER_QUERY_NAMES.length; i++) {
    const name = VOUCHER_QUERY_NAMES[i]
    const code = query.get(name)
    if (code && code !== "") {
      return code
    }
  }
}

/**
 * Filtra o componente por tipo do layout
 *
 * @param component Tipo do Layout
 * @param components Lista de componentes do layout
 * @returns Retorna TCheckoutLayoutComponent.
 */
const getLayoutComponentItem = (
  component: TCheckoutComponent,
  components?: TCheckoutLayoutComponent[]
) => {
  if (!components || components.length <= 0) {
    return undefined
  }
  return components.find((c) => c.checkoutComponent === component)
}

const getLayoutComponent = (
  component: TCheckoutComponent,
  components?: TCheckoutLayoutComponent[]
) => {
  const comp = getLayoutComponentItem(component, components)
  if (comp) {
    return comp.componentStatus
  }
  return TCheckoutLayout.None
}

const isNewLayoutCoverImage = (components?: TCheckoutLayoutComponent[]) => {
  return [
    TCheckoutLayout.Layout4,
    TCheckoutLayout.Layout5,
    TCheckoutLayout.Layout6,
    TCheckoutLayout.Layout7,
    TCheckoutLayout.Layout8,
  ].includes(getLayoutComponent(TCheckoutComponent.CoverImage, components))
}

const isLayout5ModelE = (components?: TCheckoutLayoutComponent[]) => {
  return [TCheckoutLayout.Layout5].includes(
    getLayoutComponent(TCheckoutComponent.CoverImage, components)
  )
}

const isLayout6ModelF = (components?: TCheckoutLayoutComponent[]) => {
  return [TCheckoutLayout.Layout6].includes(
    getLayoutComponent(TCheckoutComponent.CoverImage, components)
  )
}

const isLayout7ModelG = (components?: TCheckoutLayoutComponent[]) => {
  return [TCheckoutLayout.Layout7].includes(
    getLayoutComponent(TCheckoutComponent.CoverImage, components)
  )
}

const isLayout8ModelH = (components?: TCheckoutLayoutComponent[]) => {
  return [TCheckoutLayout.Layout8].includes(
    getLayoutComponent(TCheckoutComponent.CoverImage, components)
  )
}

const isStopWatchFixedTop = (list: TCustomCheckoutComponentItem[]) => {
  return (
    list.filter(
      (l) => l.type === TCustomCheckoutComponentType.stopWatch && l.fixedTop
    ).length > 0
  )
}

const getQuerySuccessPage = (
  productCodeId: string,
  productPrice: IProductPrice,
  priceAffiliated: TPriceAffiliated,
  sale: TSale,
  result: IOrderResponse,
  onlyProducer: boolean,
  charge: IOrderCharge
) => {
  var query = `?url=${result.urlRedirect || ""}&pcid=${productCodeId}&afid=${
    sale.affiliateCode ?? ""
  }`
  query += `&coid=${result.checkoutOrderId}&ocode=${result.orderCode}&pay=${
    result.paymentType || ""
  }&charge=${result.chargeId || ""}`
  query += `&sname=${priceAffiliated.sellerName}&semail=${
    priceAffiliated.sellerEmail
  }&opr=${onlyProducer ? "y" : "n"}`

  if (result.paymentType === TPaymentMethodEnum.Pix) {
    query = `${query}&code=${charge.pixQrCode}&codeurl=${charge.pixQrUrl}`
  } else if (result.paymentType === TPaymentMethodEnum.Boleto) {
    query = `${query}&code=${charge.boletoCodeBar}&codeurl=${charge.boletoCodeBarUrl}&codepdf=${charge.boletoPDF}`
  }

  query = `${query}&value=${productPrice.value || 0}&pcode=${
    productPrice.productCode
  }`
  if (priceAffiliated.pixels && priceAffiliated.pixels.length > 0) {
    const pixels = JSON.stringify(priceAffiliated.pixels)
    query = `${query}&pixels=${pixels}`

    const items: TSaleItemStorage[] = []
    items.push({
      id: productPrice.id,
      code: productPrice.codeId ?? "",
      quantity: 1,
      name: productPrice.productName ?? "",
      value: productPrice.value ?? 0,
    })

    sale.products.map((product) => {
      items.push({
        id: product.productPriceId,
        code: "",
        quantity: product.amount,
        name: product.productName,
        value: product.value,
      })
    })

    if (items && items.length > 0) {
      query = `${query}&items=${encodeURIComponent(JSON.stringify(items))}`
    }
  }

  query += `&cemail=${sale.email}&cname=${sale.name}&cphone=${
    sale.phone
  }&ccity=${sale.address.city}&cstate=${sale.address.state}&czipcode=${
    sale.address.zipCode
  }&ccountry=BR&cdoc=${numberOnly(sale.document)}`
  query += `&pname=${productPrice.productName}&gtm=${
    productPrice.googleTagManagerId ?? ""
  }`
  return query
}

const getSaleStorageByQuery = (query: URLSearchParams) => {
  const productPriceCode = query.get("pcid") ?? ""
  const productCode = query.get("pcode") ?? ""
  const affiliatedCode = query.get("afid") ?? ""
  const redirectUrl = query.get("url") ?? ""
  const sellerName = query.get("sname") ?? ""
  const sellerEmail = query.get("semail") ?? ""
  const onlyProducer = query.get("opr") === "y" ? true : false

  const checkoutOrderId = query.get("coid") ?? ""
  const orderCode = query.get("ocode") ?? ""
  const paymentType = Number(query.get("pay") ?? "0")
  const chargeId = query.get("charge") ?? ""
  const code = query.get("code") ?? ""
  const codeUrl = query.get("codeurl") ?? ""
  const codePdf = query.get("codepdf") ?? ""

  const productValue = Number(query.get("value") ?? 0)
  const pixels = query.get("pixels") ?? ""

  let items = query.get("items") ?? ""
  try {
    items = decodeURIComponent(items)
  } catch (e) {
    //console.error("Error decoding items string:", e)
  }

  const customerEmail = query.get("cemail") ?? ""
  const customerName = query.get("cname") ?? ""
  const customerPhone = query.get("cphone") ?? ""
  const customerCity = query.get("ccity") ?? ""
  const customerState = query.get("cstate") ?? ""
  const customerZipCode = query.get("czipcode") ?? ""
  const customerCountry = query.get("ccountry") ?? ""
  const customerDocument = query.get("cdoc") ?? ""

  const productName = query.get("pname") ?? ""
  const googleTagManagerId = query.get("gtm") ?? ""

  const saleStorage: TSaleStorage = {
    productPriceCode,
    productCode,
    affiliatedCode,
    redirectUrl,
    sellerName,
    sellerEmail,
    onlyProducer,
    checkoutOrderId,
    orderCode,
    paymentType,
    chargeId,
    code,
    codeUrl,
    codePdf,
    productValue,
    productName,
    googleTagManagerId,
    items: items === "" ? [] : (JSON.parse(items) as TSaleItemStorage[]),
    pixels: pixels === "" ? undefined : (JSON.parse(pixels) as TProductPixel[]),
    customer: {
      email: customerEmail,
      name: customerName,
      phone: customerPhone,
      city: customerCity,
      state: customerState,
      zipCode: customerZipCode,
      country: customerCountry,
      document: customerDocument,
    },
  }

  return saleStorage
}

const getUrlQueryThankyouPage = (url: string, saleStorage: TSaleStorage) => {
  if (!url || url === null || url === "") {
    return ""
  }

  let link = `${url}${url.includes("?") ? "&" : "?"}`
  link += `sfid=${saleStorage.checkoutOrderId}`
  if (saleStorage.onlyProducer) {
    link += "&opr=y"
  }
  return link
}

const setUserCookie = (
  customerEmail: string,
  customerName: string,
  customerPhone: string,
  customerDocument: string,
  customerCity: string,
  customerState: string,
  customerZipCode: string
) => {
  const names = customerName.toLowerCase().split(" ")
  const firstName =
    names.length > 0
      ? names.map((value, index) =>
          index < names.length - 1 ? Convertions.encryptSha256(value) : ""
        )[0]
      : ""
  const lastName =
    names.length > 1 ? Convertions.encryptSha256(names[names.length - 1]) : ""

  const data: TMetaUser = {
    em: Convertions.encryptSha256(customerEmail),
    fn: firstName,
    ln: lastName,
    ph: Convertions.encryptSha256(customerPhone),
    ei: Convertions.encryptSha256(customerDocument),
    ct: Convertions.encryptSha256(customerCity),
    st: Convertions.encryptSha256(customerState),
    zp: Convertions.encryptSha256(customerZipCode),
    name: customerName,
  }
  Cookies.set("_cefbu", JSON.stringify(data), { expires: addDays(365) })
}

const getUserCookie = () => {
  const json = Cookies.get("_cefbu")
  if (json) {
    return JSON.parse(json) as TMetaUser
  }
  return undefined
}

const addAttemptsCookie = (codeId: string) => {
  const key = codeId.toLocaleLowerCase()
  const createdDate = new Date()

  let cookie = getAttemptsCookie()
  if (cookie) {
    let item = cookie[key]
    if (item && item.crs) {
      item.crs.push(createdDate)
    } else {
      item = { crs: [createdDate] }
    }
    cookie[key] = item
  } else {
    cookie = { [key]: { crs: [createdDate] } }
  }
  Cookies.set("__celatt", JSON.stringify(cookie), { expires: addMinutes(1) })
}

const getAttemptsCookie = () => {
  const json = Cookies.get("__celatt")
  if (json) {
    return JSON.parse(json) as TCheckoutOrderAttempt
  }
  return undefined
}

const validAttemptsCookie = (codeId: string) => {
  const key = codeId.toLocaleLowerCase()
  const cookie = getAttemptsCookie()
  if (cookie) {
    const item = cookie[key]
    if (item && item.crs) {
      return item.crs.length < 3
    }
  }
  return true
}

const addAttemptsStorage = (codeId: string) => {
  try {
    const key = codeId.toLocaleLowerCase()
    const createdDate = new Date()

    let attempts = getAttemptsStorage()
    if (attempts) {
      let item = attempts[key]
      if (item && item.crs) {
        const maxDate = getMaxDate(item.crs) ?? createdDate
        const diff = diffMinutes(createdDate, new Date(maxDate))
        if (diff <= 1) {
          item.crs.push(createdDate)
        } else {
          item = { crs: [createdDate] }
        }
      } else {
        item = { crs: [createdDate] }
      }
      attempts[key] = item
    } else {
      attempts = { [key]: { crs: [createdDate] } }
    }

    window.localStorage.setItem("celatt", JSON.stringify(attempts))

    if ((attempts[key]?.crs?.length ?? 0) >= 3) {
      window.localStorage.setItem(
        `cebq${key}`,
        createdDate.getTime().toString()
      )
    } else {
      window.localStorage.removeItem(`cebq${key}`)
    }
  } catch (err) {
    console.error(err)
  }
}

const getAttemptsStorage = () => {
  try {
    const value = window.localStorage.getItem("celatt")
    if (value) {
      return JSON.parse(value) as TCheckoutOrderAttempt
    }
  } catch (err) {
    console.error(err)
  }
  return undefined
}

const validAttemptsStorage = (codeId: string) => {
  const key = codeId.toLocaleLowerCase()
  const cebq = window.localStorage.getItem(`cebq${key}`)
  if (cebq && cebq !== "") {
    const cebqTime = Number(cebq)
    const createdDate = new Date(cebqTime)
    const diff = diffMinutes(new Date(), createdDate)
    return diff > 1
  }
  return true
}

const addAttempts = (codeId: string) => {
  addAttemptsCookie(codeId)
  addAttemptsStorage(codeId)
}

const validAttempts = (codeId: string) => {
  return validAttemptsCookie(codeId) && validAttemptsStorage(codeId)
}

const askReCAPTCHA = (
  productPrice: IProductPrice | null | undefined,
  data: TSale,
  accessPageDate: Date | null
) => {
  /*if (
    !productPrice?.enableCaptcha ||
    data.payment.methodType !== methodTypeCreditCard
  ) {
    return false
  }

  if (accessPageDate) {
    const diff = diffSeconds(new Date(), accessPageDate)
    if (diff <= 30) {
      return true
    }
  }

  const nameComplet = data.name.trim().split(" ")
  if (nameComplet.length <= 2) {
    return true
  }

  return Validations.hasUpperCaseAndNumber(data.email)*/
  return true
}

const getModelByParams = (query: URLSearchParams) => {
  const modelString = numberOnly(query.get("model") ?? "")
  const modelInt = Number(modelString)
  const model = CHECKOUT_LAYOUT_ALL.find((cla) => Number(cla) === modelInt)
  return model
}

const isCheckoutCardUpdate = (purpose: string | null | undefined) => {
  return purpose?.startsWith("card-update") ?? false
}

const isCheckoutCardUpdateOnly = (purpose: string | null | undefined) => {
  return purpose?.startsWith("card-update-only") ?? false
}

const getProductValueByQuantity = (
  quantity: number,
  productPrice: IProductPrice | null | undefined
) => {
  const productValue = NumberFunctions.round(
    productPrice?.firstPurchasePrice ?? productPrice?.value ?? 0
  )
  return NumberFunctions.round(quantity * productValue)
}

const isAllowsChooseQuantity = (
  productPrice: IProductPrice | null | undefined
) => {
  return (
    productPrice?.delivery === TProductDelivery.Ticket &&
    !productPrice?.requireNominalAtCheckout
  )
}

/**
 * Conjunto de funções para ser utilizado pelo checkout
 * @property {const} getSaleDefault - Retorna valores padrão da compra com os valores externos preenchidos.
 * @property {const} getLayoutComponent - Retorna o item componente específico do layout.
 * @property {const} isNewLayoutCoverImage - Retorna se no layout 4 e 5 existe a imagem de topo
 * @property {const} isLayout5ModelE - Retorna se é o layout 5
 * @property {const} isStopWatchFixedTop - Se o temporizador é fixado no topo do site
 * @property {const} getQuerySuccessPage - Retorna os parametros (query) para a mudança de página do checkout para sucesso da cobrança
 * @property {const} getSaleStorageByQuery - Retorna o objeto para manipulação dos parametros gerado no método getQuerySuccessPage
 * @property {const} getUrlQueryThankyouPage - Retorna a URl para redirecionamento da página de sucesso caso tenha configurado no produto, com os parametros da venda
 * @property {const} setUserCookie - Grava os dados do cliente no cookie para futura compras no mesmo browser para já identificar melhor o usuário nos pixels
 * @property {const} getUserCookie - Retorna os dados do cliente que foi salvo no cookie em outra compra no método setUserCookie.
 * @property {const} askReCAPTCHA - Retorna se no checkout é para solicitar o código do ReCAPTCHA.
 * @property {const} getModelByParams - Retorna o modelo de parametro a ser utilizado no checkout por URL.
 * @property {const} getVoucherAuto - Retorna o código do cupom de desconto caso preenchido direto na URL
 * @property {const} isCheckoutCardUpdate - Retorna se o checkout que está aberto é para alteração dos das do cartão e processamento da compra.
 * @property {const} isCheckoutCardUpdateOnly - Retorna se o checkout que está aberto é apenas para alteração dos das do cartão e NÃO processamento da compra.
 */
const CheckoutFunctions = {
  /**
   * Retorna valores padrão da compra com os valores externos preenchidos.
   *
   * @param query Parametros da URL
   * @param produtctPriceId Código interno da oferta
   * @param onlyProducer Oferta imune de afiliado
   * @param subscriptionCodeId Código externo do afiliado
   * @returns Retorna TSale dados pré populados do checkout com seus valores default.
   */
  getSaleDefault,
  /**
   * Retorna o item componente específico do layout
   *
   * @param component Tipo do Layout
   * @param components Lista de componentes do layout
   * @returns Retorna TCheckoutLayoutComponent.
   */
  getLayoutComponent,
  /**
   * Retorna se no layout 4 e 5 existe a imagem de topo
   *
   * @param components Lista de componentes do layout
   * @returns Retorna `true` caso exista, `false` caso contrário.
   */
  isNewLayoutCoverImage,
  /**
   * Retorna se é o layout 5
   *
   * @param components Lista de componentes do layout
   * @returns Retorna `true` caso verdade, `false` caso contrário.
   */
  isLayout5ModelE,
  /**
   * Retorna se é o layout 6
   *
   * @param components Lista de componentes do layout
   * @returns Retorna `true` caso verdade, `false` caso contrário.
   */
  isLayout6ModelF,
  /**
   * Retorna se é o layout 7
   *
   * @param components Lista de componentes do layout
   * @returns Retorna `true` caso verdade, `false` caso contrário.
   */
  isLayout7ModelG,
  /**
   * Retorna se é o layout 8
   *
   * @param components Lista de componentes do layout
   * @returns Retorna `true` caso verdade, `false` caso contrário.
   */
  isLayout8ModelH,
  /**
   * Se o temporizador é fixado no topo do site
   *
   * @param list Lista de item de componentes do layout
   * @returns Retorna `true` caso verdade, `false` caso contrário.
   */
  isStopWatchFixedTop,
  /**
   * Retorna os parametros (query) para a mudança de página do checkout para sucesso da cobrança
   *
   * @param productCodeId Código interno da oferta
   * @param productPrice Objeto da oferta
   * @param priceAffiliated Objeto do afiliado
   * @param sale Objeto do checkout
   * @param result result da resposta do checkout
   * @param onlyProducer Venda imune de afiliado
   * @param charge Objeto da cobrança
   * @returns Retorna uma string com os parametros da query.
   */
  getQuerySuccessPage,
  /**
   * Retorna o objeto para manipulação dos parametros gerado no método getQuerySuccessPage
   *
   * @param query Parametros da URL
   * @returns Retorna TSaleStorage.
   */
  getSaleStorageByQuery,
  /**
   * Retorna a URl para redirecionamento da página de sucesso caso tenha configurado no produto, com os parametros da venda
   *
   * @param query Parametros da URL
   * @returns Retorna URL para redirecionamento adicionando novos parametros.
   */
  getUrlQueryThankyouPage,
  /**
   * Grava os dados do cliente no cookie para futura compras no mesmo browser para já identificar melhor o usuário nos pixels
   *
   * @param customerEmail E-mail do cliente
   * @param customerName Nome do cliente
   * @param customerPhone Telefone do cliente
   * @param customerDocument Documento do cliente
   * @param customerCity Cidade do cliente
   * @param customerState Estado do cliente
   * @param customerZipCode CEP do cliente
   */
  setUserCookie,
  /**
   * Retorna os dados do cliente que foi salvo no cookie em outra compra no método setUserCookie.
   *
   * @returns Retorna TMetaUser dados do cliente salvo em cookie.
   */
  getUserCookie,
  addAttempts,
  validAttempts,
  /**
   * Retorna se no checkout é para solicitar o código do ReCAPTCHA.
   *
   * @param productPrice Objeto da oferta
   * @param data Objeto do checkout
   * @param accessPageDate Data e hora acessado a página do checkout
   * @returns Retorna `true` em todos os casos.
   */
  askReCAPTCHA,
  /**
   * Retorna o modelo de parametro a ser utilizado no checkout por URL.
   *
   * @param query Parametros da URL
   * @returns Retorna o modelo do checkout TCheckoutLayout.
   */
  getModelByParams,
  /**
   * Retorna o código do cupom de desconto caso preenchido direto na URL
   *
   * @param query Parametros da URL
   * @returns Retorna o código do cupom caso exista.
   */
  getVoucherAuto,
  /**
   * Retorna se o checkout que está aberto é para alteração dos das do cartão e processamento da compra.
   *
   * @param purpose Valor do parametro purpose da query
   * @returns Retorna `true` caso purpose seja igual `card-update`, `false` caso contrário.
   */
  isCheckoutCardUpdate,
  /**
   * Retorna se o checkout que está aberto é apenas para alteração dos das do cartão e NÃO processamento da compra.
   *
   * @param purpose Valor do parametro purpose da query
   * @returns Retorna `true` caso purpose seja igual `card-update-only`, `false` caso contrário.
   */
  isCheckoutCardUpdateOnly,
  /**
   * Retorna o valor do produto multiplicado pela quantidade.
   *
   * @param quantity Quantidade do produto
   * @param productPrice Objeto da oferta
   * @returns Retorna o valor do produto multiplicado pela quantidade.
   */
  getProductValueByQuantity,
  /***
   * Retorna se é permitido escolher a quantidade do produto.
   *
   * @param productPrice Objeto da oferta
   * @returns Retorna `true` caso seja permitido, `false` caso contrário.
   */
  isAllowsChooseQuantity,
}

export default CheckoutFunctions
